/**
 * Nederlandse UI-copy voor het chat-widget.
 *
 * Centralisatie zodat:
 *   1. content-team alle teksten op één plek kan reviewen / aanpassen
 *      zonder code-context;
 *   2. een toekomstige EN/DE-vertaling alleen een nieuwe `en.ts`/`de.ts`
 *      vereist met dezelfde shape (geen refactor van components);
 *   3. consistente toon over de hele widget (één plek, één auteur).
 *
 * Convention: groep per UI-context. Functies voor copy met variabelen
 * (bv. operator-naam in begroeting). Geen ICU/MessageFormat — onnodig
 * complex voor deze schaal.
 *
 * Channel-tekst (naam, blurb, SLA-meta) staat in `i18n.channels` — de
 * *visuele* config van een kanaal (kleur, achtergrond, logoUrl) blijft in
 * channel-picker.ts (DEFAULT_CHANNELS) naast de id.
 *
 * Niet-gecentraliseerd (bewust):
 *   - Tijd-formatters (toLocaleDateString) — gebruiken Intl-API.
 */

/**
 * De naam van de virtuele assistent. Wordt geïnterpoleerd in alle copy
 * die de bot bij naam noemt (greeting, opening-prompt, balloon-meta-regel,
 * transcript-export). Host kan dit overschrijven via `chatConfig.assistantName`
 * — de getters hieronder lezen de live waarde, dus rebranden via
 * `setAssistantName('Lisa')` werkt direct voor nieuwe messages.
 *
 * NB: assistantMessages die al in state staan bevatten de tekst-snapshot
 * van het moment van schrijven. Een mid-conversation rename is voor v1
 * bewust niet gesynchroniseerd terug op die snapshots.
 */
export const DEFAULT_ASSISTANT_NAME = 'Digitale assistent';
let assistantName = DEFAULT_ASSISTANT_NAME;

export function setAssistantName(name: string): void {
  assistantName = name?.trim() || DEFAULT_ASSISTANT_NAME;
}

/** Voor tests en debugging — productie-code leest via `i18n.*`-getters. */
export function getAssistantName(): string {
  return assistantName;
}

export const i18n = {
  // ─────────────────────────── virtuele assistent ───────────────────────────
  bot: {
    /** POC: opening voor conversational intake — vraag eerst de question. */
    get openingPrompt(): string {
      return `Hallo! Ik ben ${assistantName}. Stel hieronder je vraag, dan koppel ik je daarna door aan een medewerker.`;
    },
    /** POC: na user-vraag, vraagt om de Turnstile-bevestiging. Komt vóór
     *  `askNameAfterQuestion` als de service captcha vereist. B1. Bij
     *  succesvolle verificatie krijgt deze balloon `askCaptchaDoneSuffix`
     *  appended (geen aparte balloon — minder visuele ruis). */
    askCaptcha:
      'Voordat we verder gaan, even bevestigen dat je geen robot bent…',
    /** Suffix die de reducer aan `askCaptcha` plakt zodra CAPTCHA_VERIFIED
     *  binnenkomt. Bewust een spatie + emoji zodat de overgang voelt als
     *  "afgevinkt" naast de oorspronkelijke vraag i.p.v. een nieuwe regel. */
    askCaptchaDoneSuffix: ' ✅',
    /** POC: bot meldt dat we de host-portal aftoetsen op een actieve login
     *  voordat we beslissen of er nog captcha + naam/e-mail nodig is. Komt
     *  direct ná de eerste user-vraag (asking-question → awaiting-identity)
     *  en alleen wanneer de identity-check nog niet resolved is. */
    checkingIdentity:
      'Een moment, ik kijk even of je al ingelogd bent…',
    /** Reden-specifieke system-pills wanneer een session-start faalt — komt
     *  in de chat-stream zodat de bezoeker zijn context behoudt en weet
     *  wat er gebeurde. Composer wordt direct daarna terug-enabled zodat
     *  hij opnieuw kan proberen. */
    sessionFailedNetwork:
      'Geen verbinding met de chatdienst. Controleer je internet en probeer het opnieuw.',
    sessionFailedCaptcha:
      'De beveiligingscheck is niet gelukt. Probeer het opnieuw.',
    sessionFailedCaptchaSystem:
      'De beveiligingscheck antwoordt niet. Probeer het opnieuw of probeer het later.',
    sessionFailedUnavailable:
      'De chatdienst is tijdelijk niet beschikbaar. Probeer het later opnieuw.',
    sessionFailedUnknown:
      'Er ging iets mis bij het starten van de chat. Probeer het opnieuw.',
    /** POC: vervolg op `openingPrompt` ná de eerste user-vraag (of na
     *  captcha-success). Geen re-introductie meer. B1. */
    askNameAfterQuestion:
      'Bedankt voor je vraag. Voordat ik je doorverbind met een medewerker, wat is je naam?',
    get greeting(): string {
      return `Hallo! Ik ben ${assistantName}. Voordat ik je doorverbind aan een medewerker — wat is je naam?`;
    },
    /** Na ontvangst van de naam: bedankt + signaleert "laatste vraag"
     *  zodat de bezoeker weet dat de bot-Q&A daarna afgelopen is. B1. */
    thanks: (name: string) =>
      `Dankjewel ${name}. Nog één ding: wat is je e-mailadres?`,
    invalidNameTooShort: 'Vul alsjeblieft minimaal 2 letters in.',
    invalidNameTooLong: 'Dat lijkt te lang voor een naam. Vul een kortere naam in.',
    invalidEmail:
      'Dat lijkt geen geldig e-mailadres. Probeer het opnieuw.',
    searching: 'Dankjewel! Ik zoek nu een medewerker voor je…',
    /** Voor terugkerende bezoekers — slaat naam/e-mail-prompts over. */
    welcomeBack: (name: string) =>
      `Welkom terug, ${name}! Ik zoek nu een medewerker voor je…`,
    operatorAttachedSystem: (name: string) =>
      `${name} heeft je vraag opgepakt`,
    /**
     * Bot-aankondiging vlak voordat de operator daadwerkelijk wordt
     * gekoppeld — geeft de bezoeker een natural-language overgang in
     * plaats van een schoksgewijs UI-omslag.
     */
    operatorFound: (name: string) =>
      `Goed nieuws, ${name} is beschikbaar! Ik koppel je nu door.`,

    // ─── AVAA intake (authorized repair-request) ─────────────────────────
    /** Meerdere actieve contracten: keuzevraag boven de inline
     *  contract-choice-cards in de chat-stream. */
    avaaContractPrompt:
      'Hallo! Ik ben je AI assistent en help je graag met je reparatieverzoek. Over welk contract gaat het?',
    /** Eén actief contract: de picker wordt overgeslagen en de assistent
     *  begroet meteen met het adres van die woning. */
    avaaSingleGreeting: (address: string) =>
      `Hallo! Ik ben je AI assistent en help je graag met je reparatieverzoek voor ${address}. Waar kan ik je mee helpen?`,
    /** Vervolg-prompt zodra de bezoeker (bij meerdere contracten) een
     *  contract heeft gekozen. Composer ontgrendelt; bezoeker beschrijft
     *  het probleem. */
    avaaGreeting:
      'Dankjewel, waar kan ik je mee helpen?',
  },

  // ─────────────────────────── AAVA-intake (server-side AI) ──────────────
  aava: {
    /** Hint in de composer wanneer AAVA aan het verwerken is. Gebruikt de
     *  ingestelde assistent-naam (chatConfig.assistantName) i.p.v. een
     *  hardcoded merknaam. */
    get replying(): string {
      return `${assistantName} is aan het denken…`;
    },
    /** Permanente AI-disclaimer onderaan de composer (zelfde regio als
     *  `replying`), getoond zodra de conversation loopt en de bezoeker mag
     *  typen — d.w.z. wanneer de server een bericht heeft teruggestuurd. */
    disclaimerShort: 'Je chat met een AI-assistent · antwoorden kunnen fouten bevatten',
    /** Titel (summary) van de inklapbare gebruiksvoorwaarden bovenaan een
     *  nieuwe AAVA-chat. */
    disclaimerLongTitle: 'Gebruiksvoorwaarden AI-assistent',
    /** Volledige gebruiksvoorwaarden, getoond in een inklapbaar blok bovenaan
     *  de AAVA-chat. `org` = naam van de woningcorporatie (chatConfig.orgName)
     *  en vervangt elke `[Woningcorporatie]`-placeholder. De `[spoednummer]`-
     *  en `[kanaal/livechat]`-placeholders blijven nog literal — die moeten
     *  nog configureerbaar worden (zie TODO bij de aanroep). */
    disclaimerLong: (
      org: string,
      emergencyNumber?: string,
      supportChannel?: string,
    ): string => {
      // Configureerbare velden (chatConfig) met nette fallbacks zodat er nooit
      // een letterlijke [placeholder] in beeld komt als ze leeg zijn.
      const sos = emergencyNumber?.trim() || `het storingsnummer van ${org}`;
      const support = supportChannel?.trim() || 'telefoon of e-mail';
      return `${org} biedt een AI-assistent aan waarmee je een reparatie- of onderhoudsmelding kunt doen en algemene informatie kunt krijgen. Door de AI-assistent te gebruiken en berichten te sturen via de chat, ga je akkoord met onderstaande voorwaarden. In de privacyverklaring van ${org} lees je hoe wij met je persoonsgegevens omgaan. Je communiceert in deze chat met een AI-systeem en niet met een medewerker, tenzij je gesprek wordt overgedragen aan de klantenservice.

1. Doel en algemene informatie
Beoogde gebruikers: De AI-assistent is bedoeld voor huurders van ${org} van 18 jaar of ouder, en voor personen die een reparatie willen melden of informatie zoeken over onze dienstverlening. De AI-assistent is niet bedoeld voor kinderen en niet voor gezondheidsgerelateerde vragen.
Functionaliteit en doel: De AI-assistent maakt gebruik van kunstmatige intelligentie (AI). Hij stelt vragen en kan tekst, foto's en video die je aanlevert analyseren, en helpt zo om je reparatieverzoek vast te leggen en een eerste inschatting (triage) van de mogelijke oorzaak te maken. De AI-assistent neemt zelf geen besluit over of, hoe en wanneer een reparatie wordt uitgevoerd. Die beoordeling en beslissing worden altijd door mensen genomen, bijvoorbeeld door een medewerker of een vakbekwame professional. De uitkomsten van de AI-assistent zijn ondersteunend en worden door een mens beoordeeld voordat er werkzaamheden plaatsvinden.
Geen bindende toezeggingen: Antwoorden van de AI-assistent zijn informatief en vormen geen juridisch bindende aanbiedingen, garanties of overeenkomsten namens ${org}. De AI-assistent verwerkt geen financiële transacties en doet geen bindende toezeggingen. Controleer voorwaarden, kosten en afspraken altijd aan de hand van de officiële informatie van ${org}.
Spoed en onveilige situaties: Gebruik de AI-assistent niet voor spoedeisende of gevaarlijke situaties, zoals een gaslek, wateroverlast of een acuut onveilige situatie. Neem in die gevallen direct telefonisch contact op via ${sos}, of bel bij direct gevaar 112.
Accountspecifieke vragen: Voor het doen van een reparatieverzoek log je in tijdens het chatgesprek. Zonder inloggen kan de AI-assistent je alleen met algemene informatie helpen. De AI-assistent kan je bij een beperkt aantal accountgerelateerde zaken ondersteunen.
Ondersteuning door een medewerker: Voor vragen die de AI-assistent niet kan beantwoorden of afhandelen, kun je contact opnemen met een medewerker van de klantenservice via ${support}. Je kunt er ook altijd voor kiezen om je melding rechtstreeks per telefoon of e-mail bij ${org} te doen, zonder de AI-assistent te gebruiken.

2. Beperkingen
Beschikbaarheid: We streven naar continue beschikbaarheid, maar de AI-assistent kan tijdelijk niet beschikbaar zijn door onderhoud of updates.
Mogelijke fouten en grenzen: De AI-assistent kan fouten maken. Hij kan onduidelijke, slecht belichte of lage-resolutiebeelden verkeerd interpreteren, context missen die je niet hebt meegegeven, en zeldzame of ongebruikelijke gebreken mogelijk niet herkennen. De AI-assistent is geen vervanging voor het oordeel van een vakbekwame professional en mag niet worden gebruikt als basis voor elektra- of gaswerkzaamheden door personen zonder de juiste kwalificatie.
Taalondersteuning: De AI-assistent ondersteunt meerdere talen. Vertalingen worden voor je gemak aangeboden. Bij verschillen tussen taalversies van deze voorwaarden is de Nederlandse versie leidend.

3. Verantwoordelijkheden van de gebruiker
Correct gebruik: Ga respectvol om met de AI-assistent. Misbruik, manipulatie of frauduleus gebruik van de AI-assistent is niet toegestaan.
Juiste en veilige informatie: Geef juiste en volledige informatie, zodat de AI-assistent een goede inschatting kan maken. Deel geen gevoelige persoonsgegevens die niet nodig zijn, zoals financiële of gezondheidsgegevens. Houd je inloggegevens geheim en deel ze niet met anderen.

4. Persoonsgegevens
${org} is verwerkingsverantwoordelijke voor de verwerking van je persoonsgegevens. De AI-assistent wordt geleverd door Embrace (the Human Cloud) als verwerker, op basis van het Aidenn-systeem. Je gegevens worden binnen de Europese Unie opgeslagen (AWS, Frankfurt). Je persoonsgegevens worden niet gebruikt om het AI-model te trainen, en gegevens worden niet langer bewaard dan nodig is. Hoe wij met je persoonsgegevens omgaan, lees je in de privacyverklaring van ${org}.

5. Verantwoordelijkheid van ${org}
De AI-assistent kan fouten maken. Als er een fout optreedt, neemt ${org} redelijke maatregelen om dit te herstellen. Omdat de uiteindelijke beoordeling van een reparatie altijd door een mens wordt gedaan, blijft menselijk toezicht onderdeel van het proces. ${org} is aansprakelijk voor schade die opzettelijk of door nalatigheid is veroorzaakt, en overigens volgens de wettelijke bepalingen. Je bent verplicht om eventuele schade zoveel mogelijk te beperken.

6. Toepasselijk recht
Op deze gebruiksvoorwaarden is Nederlands recht van toepassing. Geschillen die voortvloeien uit het gebruik van de AI-assistent worden volgens Nederlands recht beslecht.`;
    },
    /** Generieke foutmelding wanneer een AAVA-call faalt. */
    errorNetwork:
      'Geen verbinding met de AI-assistent. Controleer je internetverbinding.',
    errorUnauthorized:
      'Je sessie is verlopen. Log opnieuw in om verder te chatten.',
    errorUnavailable:
      'De AI-assistent is nu niet beschikbaar. Probeer het later opnieuw.',
    errorParse:
      'Onverwacht antwoord van de AI-assistent. Probeer het later opnieuw.',
    /** Slotbericht zodra de conversation server-side is afgerond; bezoeker wordt doorgestuurd. */
    completed:
      'Gesprek voltooid! Je wordt doorgestuurd naar het overzicht van je reparatieverzoek.',
    /**
     * Self-fix-vraag: getoond wanneer de AI-assistent het probleem tijdens de
     * chat heeft opgelost (`outcome: Resolved`). De bezoeker bevestigt met
     * ja/nee of het daadwerkelijk verholpen is.
     */
    selfFix: {
      question: 'Is je probleem hiermee opgelost?',
      confirm: 'Ja, opgelost',
      decline: 'Nee, niet opgelost',
      /** Excuusbericht na "Nee"; kort daarna volgt de redirect naar de RR-pagina. */
      apology:
        'Wat vervelend dat het probleem nog niet is opgelost. We sturen je door naar de pagina waar je een reparatieverzoek kunt indienen.',
    },
  },

  // ─────────────────────────── header ───────────────────────────
  header: {
    hero: 'Hallo, hoe kunnen we je helpen?',
    heroSub: 'Kies hieronder hoe we je kunnen helpen',
    intake: 'Stel je vraag en start het gesprek',
    intakeSub: 'We koppelen je aan een medewerker',
    closed: 'Chat is nu gesloten',
    get assistant(): string { return assistantName; },
    assistantSub: 'We helpen je graag op weg',
    agentSub: 'Online · reageert meestal binnen enkele minuten',
    endedSub: 'Gesprek beëindigd',
    fallbackOperator: 'Klantenservice',
    btnBack: 'Terug',
    btnClose: 'Minimaliseer chat',
    btnDownload: 'Download transcript',
    authRequired: 'Reparatie indienen',
    authRequiredSub: 'Inloggen vereist',
  },

  // ─────────────────────────── channel-picker ─────────────────────
  picker: {
    /** Grouping-rij bij gemengde channels (chats + diensten). */
    chatGroup: 'Chatten met medewerker',
    chatGroupBlurb: 'Kies een chat-kanaal',
    /** Status-pill labels naast een kanaal-naam in de picker. */
    statusOnline: 'Online',
    statusOffline: 'Offline',
  },

  // ─────────────────────────── channels (tekst) ───────────────────
  /**
   * Per-kanaal tekst (naam, blurb, SLA-meta). Gemigreerd vanuit
   * channel-picker.ts zodat alle UI-copy op één plek staat en een
   * toekomstige EN/DE-vertaling deze ook dekt. De *visuele* config
   * (kleur, achtergrond, logoUrl) blijft in `channel-picker.ts`
   * (DEFAULT_CHANNELS) naast de id.
   */
  channels: {
    webchat: {
      name: 'Webchat',
      blurb: 'Direct live chatten',
      blurbOffline: 'Chat is nu gesloten',
      meta: 'Meestal binnen enkele minuten',
    },
    'repair-request': {
      name: 'Reparatie indienen',
      blurb: 'Meld een reparatieverzoek bij de woningcorporatie',
      meta: 'Inloggen vereist',
    },
    whatsapp: {
      name: 'WhatsApp',
      blurb: 'Chat in je eigen tempo • geen wachtrij',
      meta: 'Antwoord binnen 2 uur',
    },
    messenger: {
      name: 'Facebook Messenger',
      blurb: 'Stuur een bericht, reageer wanneer het uitkomt',
      meta: 'Antwoord binnen 4 uur',
    },
  },

  // ─────────────────────────── composer ───────────────────────────
  composer: {
    placeholderDefault: 'Typ een bericht…',
    placeholderName: 'Typ je naam…',
    placeholderEmail: 'Typ je e-mailadres…',
    placeholderRepairQuestion: 'Beschrijf je reparatieverzoek…',
    btnAttach: 'Bestand toevoegen',
    btnEmoji: 'Emoji invoegen',
    btnSend: 'Verstuur bericht',
    disabledHintEnded:
      'Dit gesprek is gesloten — nieuwe berichten zijn uitgeschakeld.',
    /** Composer-disabled-hints per assistant.step (alleen de steps waar
     *  de composer disabled is en een uitleg toont). */
    disabledHintAwaitingCaptcha:
      'Eerst de captcha-bevestiging voltooien hierboven.',
    disabledHintAwaitingIdentity:
      'Een moment, we controleren of je ingelogd bent…',
  },

  // ─────────────────────────── settings-menu (actions) ───────────────────
  settings: {
    btnOpen: 'Menu',
    title: 'Menu',
    btnDownload: 'Download transcript',
    btnCloseConversation: 'Chatgesprek sluiten',
    btnForget: 'Mij vergeten',
    /** Fallback wanneer geen enkele actie van toepassing is — zou
     *  in praktijk niet vaak voorkomen omdat het menu dan helemaal
     *  niet getoond wordt, maar voor zekerheid. */
    emptyHint: 'Geen acties beschikbaar.',
    confirmTitle: 'Weet je het zeker?',
    /** subject = "Remco (remco@example.nl)" of leeg-string als visitor
     *  niet bekend is. Body verwerkt beide gevallen. */
    confirmBody: (subject: string) =>
      subject
        ? `We vergeten ${subject} op dit apparaat. Een lopend gesprek wordt afgesloten.`
        : 'Je gegevens worden van dit apparaat verwijderd. Een lopend gesprek wordt afgesloten.',
    confirmYes: 'Ja, vergeet me',
    confirmCancel: 'Annuleren',
    privacyHint:
      'Tip: je gegevens zijn opgeslagen voor volgende keer. Beheer via het menu rechtsboven.',
  },

  // ─────────────────────────── contract-choices (AVAA-intake) ──────────
  contracts: {
    title: 'Voor welke woning?',
    subtitle: 'Kies de woning waarover je je reparatieverzoek wilt indienen.',
    loading: 'Contracten ophalen…',
    empty: 'We konden geen actieve contracten vinden onder jouw account.',
    errorUnauthorized:
      'Je sessie is verlopen. Log opnieuw in om je contracten te zien.',
    errorUnavailable:
      'Contracten zijn nu niet beschikbaar. Probeer het later opnieuw.',
    errorNetwork:
      'Geen verbinding met de contract-service. Controleer je internetverbinding.',
    errorParse:
      'Onverwacht antwoord van de server bij het ophalen van je contracten.',
    btnRetry: 'Opnieuw proberen',
    /** Fallback-naam wanneer adresvelden ontbreken in de unit. */
    unitFallback: (id: string) => `Eenheid ${id.slice(0, 8)}`,
    /** Zichtbare klik-affordance op een contract-keuzekaart. */
    chooseCue: 'Kies',
    /** Toegankelijke knop-naam per contract-keuzekaart (WCAG). */
    chooseAria: (address: string) => `Kies woning ${address}`,
    /** Periode-formatting voor contractkaart. */
    period: (start: string, end: string | null) =>
      end ? `${start} – ${end}` : `Vanaf ${start}`,
    /** Composer-disabled-hint wanneer nog geen contract gekozen — in
     *  de huidige inline-flow zie je de keuzes ín de chat-stream. */
    selectFirstHint: 'Kies eerst een woning in het gesprek.',
  },

  // ─────────────────────────── auth-required (repair-channel) ──────────
  authRequired: {
    title: 'Inloggen vereist',
    body:
      'Om een reparatieverzoek in te dienen moet je ingelogd zijn op het ' +
      'klantportaal. Log in om door te gaan.',
    /** Hoofd-CTA. Klik triggert standaard een terugkeer naar de channel-picker.
     *  Host kan via `window.chatConfig.repairLoginUrl` een externe inlog-link
     *  injecteren — dan opent deze knop die URL i.p.v. terug te navigeren. */
    btnPrimary: 'Inloggen',
    btnSecondary: 'Terug naar overzicht',
  },

  // ─────────────────────────── ended-banner ───────────────────────────
  endedBanner: {
    title: 'Dit gesprek is beëindigd',
    subtitle: 'Download het transcript of sluit het gesprek.',
    btnTranscript: 'Transcript',
    btnClose: 'Sluit gesprek',
  },

  // ─────────────────────────── searching-card ───────────────────────────
  /**
   * Status-teksten voor de in-stream "we zoeken een medewerker"-card.
   * De drie varianten draaien op een tijdsdrempel zodat de wacht zich
   * niet als statisch voelt: na 10s "verder zoeken", na 30s "drukker".
   */
  searching: {
    statusInitial: 'Op zoek naar een beschikbare medewerker…',
    statusMid: 'We zoeken nog even verder…',
    statusBusy: 'Het is wat drukker dan normaal.',
  },

  // ─────────────────────────── rating ───────────────────────────
  rating: (() => {
    const sterren = (n: number): string => (n === 1 ? 'ster' : 'sterren');
    return {
      /**
       * Conversationele intro die boven de sterren-form staat —
       * alsof de digitale assistent de bezoeker vraagt om feedback.
       * Past visueel onder de meta-regel van eerdere bot-pills.
       */
      botIntro:
        'Bedankt voor het gesprek! Mag ik je nog één laatste vraag stellen om onze service te verbeteren?',
      title: 'Hoe tevreden was je met dit gesprek?',
      subtitle: 'Optioneel · helpt ons onze service te verbeteren',
      hint: 'Tik op een ster om te beoordelen',
      /** Index 0..4 = sterren 1..5. */
      labels: ['Slecht', 'Matig', 'Oké', 'Tevreden', 'Geweldig'],
      btnSubmit: 'Verstuur',
      successTitle: 'Bedankt voor je feedback!',
      successText: (n: number) =>
        `Je gaf dit gesprek ${n} ${sterren(n)}. We gebruiken dit om onze service te verbeteren.`,
      starAria: (n: number) => `${n} ${sterren(n)} geven`,
    };
  })(),

  // ─────────────────────────── message-list ───────────────────────────
  messages: {
    today: 'Vandaag',
    yesterday: 'Gisteren',
    statusGelezen: 'Gelezen',
    statusVerzonden: 'Verzonden',
    statusVerzenden: 'Verzenden…',
    statusFailed: 'Niet verzonden',
    btnRetry: 'Opnieuw verzenden',
    btnRetryAria: 'Bericht opnieuw verzenden',
    get botLabel(): string { return assistantName; },
    endedAt: (time: string, by: string | null) =>
      by ? `Gesprek beëindigd om ${time} · door ${by}` : `Gesprek beëindigd om ${time}`,
    endedByJou: 'jou',
  },

  // ─────────────────────────── transcript ───────────────────────────
  transcript: {
    titleFor: (org: string) => `Chat-transcript — ${org}`,
    dateLabel: 'Datum',
    medewerkerLabel: (name: string, role: string) =>
      `Medewerker: ${name} (${role})`,
    fallbackOperator: 'Medewerker',
    labelJij: 'Jij',
    labelSysteem: 'Systeem',
    get labelDigitaleAssistent(): string { return assistantName; },
    fileLine: (name: string, sizeLabel: string) =>
      `→ bestand: ${name} (${sizeLabel})`,
    end: 'Einde transcript',
  },

  // ─────────────────────────── miscellaneous ───────────────────────────
  misc: {
    bootstrap: 'Verbinding maken…',
    identifying: 'Even controleren of je al ingelogd bent…',
    connectingOperator: 'Verbinden met een medewerker…',
    fileSizeUnknown: 'Onbekend',
    sessionRejectedFallback: 'Het gesprek kon niet gestart worden.',
    launcherOpen: 'Open chat',
    launcherMinimize: 'Minimaliseer chat',
    indicatorOnline: 'Chat is online',
    indicatorOffline: 'Chat is gesloten',
    unreadAria: (count: number) =>
      `${count} nieuw${count === 1 ? ' bericht' : 'e berichten'}`,
  },
};
